import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Check, Shield, TrendingUp, Banknote,
  Network, Package, FileText, ShieldCheck, Sparkles, Bot, Zap,
  BarChart3, Users, Globe, LineChart, Landmark, Clock,
} from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

/* verqo — Investor / credit facility pitch page.
   Public URL. Shareable. One scrollable document — not a slide deck.
   Edit the facility size ($25M), email (founders@verqo.com), and stats
   in the relevant arrays before sending to a specific investor. */

export default function InvestorPitch() {
  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-40 backdrop-blur-md bg-background/85 border-b border-foreground/10">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-foreground grid place-items-center">
              <span className="text-accent font-bold text-[13px] display">v</span>
            </div>
            <span className="display font-semibold text-[16px] tracking-tight">verqo</span>
            <span className="text-foreground/40 mx-1.5">/</span>
            <span className="display text-[15px] tracking-tight">Credit facility</span>
          </Link>
          <div className="flex items-center gap-3 text-[12px]">
            <a href="#ask" className="text-foreground/60 hover:text-foreground">The ask</a>
            <a href="#economics" className="text-foreground/60 hover:text-foreground">Economics</a>
            <a href="#contact" data-testid="pitch-contact-cta" className="inline-flex items-center gap-1 text-foreground font-medium">
              Contact <ArrowUpRight className="w-3.5 h-3.5"/>
            </a>
          </div>
        </div>
      </div>

      {/* ────────────────────────── HERO ────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease }}
        >
          <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/55 inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-accent shadow-[0_0_10px_var(--accent,#e8ff3a)]"/>
            Credit facility · Confidential · For investor review
          </div>
          <h1 className="display text-5xl sm:text-6xl md:text-7xl mt-5 leading-[1.02] tracking-tight max-w-5xl">
            Fund the rails behind the<br/>
            <span className="relative">
              $10T wholesale economy
              <span className="absolute left-0 -bottom-1 h-[3px] w-full bg-accent"/>
            </span>.
          </h1>
          <p className="text-foreground/65 mt-6 text-[17px] leading-relaxed max-w-2xl">
            verqo is the operating system for B2B wholesale — embedded credit, payments, insurance
            and shipping, one portal for brands and their retail buyers. We're raising a
            <strong className="text-foreground"> revolving credit facility</strong> to fund
            insured buyer receivables, inventory, and PO financing across the platform.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href="#ask" data-testid="pitch-see-ask">
              <button className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-12 px-6 text-[14px] font-medium inline-flex items-center gap-1 transition-colors">
                See the ask <ArrowRight className="w-4 h-4"/>
              </button>
            </a>
            <Link to="/demo">
              <button className="rounded-full h-12 px-6 text-[14px] font-medium border border-foreground/20 hover:bg-foreground/5 transition-colors inline-flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-accent fill-accent"/> Live portal demo
              </button>
            </Link>
          </div>

          {/* Big stats strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { v: "$25M",      k: "Facility sought" },
              { v: "~18%",      k: "Gross yield on AR" },
              { v: "< 0.4%",    k: "Net default rate" },
              { v: "52 days",   k: "Avg receivable tenor" },
            ].map((s, i) => (
              <motion.div
                key={s.k}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 + i * 0.07, ease }}
                className="rounded-2xl border border-foreground/10 bg-white/60 p-5"
              >
                <div className="display text-3xl md:text-4xl tracking-tight">{s.v}</div>
                <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 mt-2">{s.k}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ────────────────────────── PROBLEM ────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-20 border-t border-foreground/10">
        <Eyebrow>01 · The problem</Eyebrow>
        <h2 className="display text-4xl sm:text-5xl mt-3 leading-[1.05] tracking-tight max-w-3xl">
          A $10T market still runs on PDFs, fax, and factoring.
        </h2>
        <p className="text-foreground/65 mt-5 text-[16px] leading-relaxed max-w-2xl">
          Wholesale commerce is the last trillion-dollar market that never got digitized.
          Every existing solution solves one slice — catalogs, or checkout, or credit — nobody
          has built the operating system.
        </p>

        <div className="mt-12 grid md:grid-cols-3 gap-4">
          {[
            { n: "3–5%", t: "average factoring fee sellers pay just to get paid on time" },
            { n: "47 days", t: "average wait from PO to payment in wholesale today" },
            { n: "98%", t: "of wholesale invoices are uninsured — one bad buyer kills a brand" },
          ].map((p, i) => (
            <motion.div
              key={p.t}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.08, ease }}
              className="rounded-2xl border border-foreground/10 bg-white/60 p-7"
            >
              <div className="display text-5xl tracking-tight text-foreground">{p.n}</div>
              <div className="text-foreground/70 mt-3 text-[14.5px] leading-relaxed">{p.t}</div>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 text-[14.5px] text-foreground/60 max-w-2xl">
          <strong className="text-foreground">Faire solved discovery. Joor solved catalogs.</strong> Nobody has built the rails — payments, credit, insurance, shipping — into one platform. That's the vacuum.
        </div>
      </section>

      {/* ────────────────────────── SOLUTION ────────────────────────── */}
      <section className="relative py-24 border-t border-foreground/10 overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-[0.04]"/>
        <div className="max-w-7xl mx-auto px-6 relative">
          <Eyebrow>02 · Our solution</Eyebrow>
          <h2 className="display text-4xl sm:text-5xl mt-3 leading-[1.05] tracking-tight max-w-3xl">
            Shopify + Stripe + a factor + insurance.<br/>
            <span className="text-foreground/40">One login. One portal. Every deal.</span>
          </h2>

          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {[
              { Icon: Banknote,     title: "For sellers",  line: "Get paid D+1 on every invoice. We assume the buyer-default risk.", tag: "Sell more" },
              { Icon: ShieldCheck,  title: "For buyers",   line: "One credit line across every supplier. Approved in under 2 minutes.", tag: "Buy now, pay later" },
              { Icon: Network,      title: "For verqo",    line: "Fees on payments + credit + insurance + shipping on every transaction.", tag: "4 revenue streams" },
            ].map((c, i) => (
              <motion.div key={c.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.08, ease }}
                className="rounded-2xl bg-foreground text-background p-7 relative overflow-hidden"
              >
                <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-accent/20 blur-2xl"/>
                <div className="relative">
                  <div className="w-11 h-11 rounded-xl bg-accent/20 ring-1 ring-accent/40 grid place-items-center">
                    <c.Icon className="w-5 h-5 text-accent"/>
                  </div>
                  <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-accent/80 mt-5">{c.tag}</div>
                  <div className="display text-xl mt-2 tracking-tight">{c.title}</div>
                  <p className="text-background/70 mt-3 text-[14px] leading-relaxed">{c.line}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────────── WHY NOW ────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-20 border-t border-foreground/10">
        <Eyebrow>03 · Why now</Eyebrow>
        <h2 className="display text-4xl sm:text-5xl mt-3 leading-[1.05] tracking-tight max-w-3xl">
          This couldn't have been built 5 years ago.
        </h2>
        <div className="mt-12 grid md:grid-cols-2 gap-4">
          {[
            { Icon: Zap,        t: "Embedded finance infrastructure finally exists.", d: "Plaid, Modern Treasury, Middesk — all post-2020. We plug into rails that would have cost $10M to build in-house." },
            { Icon: Bot,        t: "AI-grade underwriting.",                           d: "Our Verqo AI underwriter reads Plaid transactions, KYB data and behavioral signals in 90 seconds — matching credit committees that take days." },
            { Icon: TrendingUp, t: "Brands are bleeding margin.",                      d: "Higher rates + inflation + DTC saturation mean wholesale brands need embedded terms to stay competitive. Urgency is real." },
            { Icon: Globe,      t: "Faire proved the TAM. We own the stack.",          d: "Faire hit $12B as a marketplace. We're the SaaS + rails layer — 10× their ARR per customer." },
          ].map((x, i) => (
            <motion.div key={x.t}
              initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.07, ease }}
              className="rounded-2xl border border-foreground/10 bg-white/60 p-7 flex gap-5"
            >
              <div className="w-11 h-11 rounded-xl bg-foreground text-accent grid place-items-center shrink-0">
                <x.Icon className="w-5 h-5"/>
              </div>
              <div>
                <div className="display text-lg tracking-tight">{x.t}</div>
                <p className="text-foreground/65 mt-2 text-[14px] leading-relaxed">{x.d}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ────────────────────────── BUSINESS MODEL ────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-20 border-t border-foreground/10">
        <Eyebrow>04 · Business model</Eyebrow>
        <h2 className="display text-4xl sm:text-5xl mt-3 leading-[1.05] tracking-tight max-w-3xl">
          Four revenue streams on every wholesale transaction.
        </h2>
        <p className="text-foreground/65 mt-5 text-[16px] leading-relaxed max-w-2xl">
          Per $1M GMV processed, verqo captures ~$35K. Average seller processes $2–5M/yr — blended ARR per seller: $70K–$175K.
        </p>

        <div className="mt-10 rounded-2xl border border-foreground/10 bg-white overflow-hidden">
          <table className="w-full text-[14px]">
            <thead className="bg-secondary/40 text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">
              <tr>
                <th className="text-left p-4">Stream</th>
                <th className="text-left p-4">Take-rate</th>
                <th className="text-right p-4">Per $1M GMV</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-foreground/5">
              {[
                ["Payments (ACH / card)",                        "1.0%",      "$10,000"],
                ["Credit spread (charge 1.2%/mo · borrow 0.7%)", "1.5–2.0%",  "$15,000"],
                ["Trade insurance commission",                    "0.5–1.0%",  "$7,500"],
                ["SaaS subscription",                             "$199–$999/mo", "$2,400–$12K/yr"],
              ].map((r, i) => (
                <tr key={i} className="hover:bg-secondary/20">
                  <td className="p-4 font-medium">{r[0]}</td>
                  <td className="p-4 text-foreground/80">{r[1]}</td>
                  <td className="p-4 text-right display">{r[2]}</td>
                </tr>
              ))}
              <tr className="bg-foreground text-background">
                <td className="p-4 font-medium">ARR per active seller</td>
                <td className="p-4"></td>
                <td className="p-4 text-right display text-lg">~$35K–$175K</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* ────────────────────────── TARGET / GROWTH ────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-20 border-t border-foreground/10">
        <Eyebrow>05 · Target market · path to $30M GMV</Eyebrow>
        <h2 className="display text-4xl sm:text-5xl mt-3 leading-[1.05] tracking-tight max-w-3xl">
          Beachhead: US beauty & personal care brands.
        </h2>
        <p className="text-foreground/65 mt-5 text-[16px] leading-relaxed max-w-2xl">
          12,000 active wholesale brands. Avg $1.2M GMV each. Paying factors 4% + waiting 30 days to get funded. Our target is 50 anchor brands × $600K GMV each = <strong className="text-foreground">$30M GMV in 12 months.</strong>
        </p>

        <div className="mt-10 grid grid-cols-4 gap-3">
          {[
            { q: "Q1", s: "10 anchors",  gmv: "$1.2M",  run: "$400K" },
            { q: "Q2", s: "25 sellers",  gmv: "$5.6M",  run: "$1.4M" },
            { q: "Q3", s: "40 sellers",  gmv: "$14.4M", run: "$3.2M" },
            { q: "Q4", s: "60 sellers",  gmv: "$30M",   run: "$7.2M" },
          ].map((c, i) => (
            <motion.div key={c.q}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.08, ease }}
              className={`rounded-2xl p-5 ${i === 3 ? "bg-foreground text-background" : "border border-foreground/10 bg-white/60"}`}
            >
              <div className={`text-[10.5px] mono uppercase tracking-[0.18em] ${i === 3 ? "text-accent" : "text-foreground/55"}`}>{c.q}</div>
              <div className="display text-3xl tracking-tight mt-2">{c.gmv}</div>
              <div className={`text-[12px] mt-1 ${i === 3 ? "text-background/65" : "text-foreground/55"}`}>cumulative</div>
              <div className="mt-4 text-[12px]">{c.s}</div>
              <div className={`text-[11px] mono uppercase tracking-[0.14em] mt-1 ${i === 3 ? "text-accent/80" : "text-foreground/50"}`}>{c.run} MRR</div>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-foreground/10 bg-white/60 p-6">
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Go-to-market</div>
            <ul className="mt-3 space-y-2.5 text-[14px] text-foreground/80">
              <li className="flex gap-2"><Check className="w-4 h-4 text-foreground mt-0.5 shrink-0"/>Trade-show wedge: MAGIC, IBE, Cosmoprof — 300 qualified leads per season</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-foreground mt-0.5 shrink-0"/>Seller-led buyer acquisition: each seller brings 10–50 buyers via our invite system</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-foreground mt-0.5 shrink-0"/>Partnerships: wholesale agencies + industry associations</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-foreground mt-0.5 shrink-0"/>Category ownership: coin "Wholesale OS" and own the SEO</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-foreground/10 bg-white/60 p-6">
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Expansion</div>
            <ul className="mt-3 space-y-2.5 text-[14px] text-foreground/80">
              <li className="flex gap-2"><Check className="w-4 h-4 text-foreground mt-0.5 shrink-0"/>Q4: fashion + home goods verticals</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-foreground mt-0.5 shrink-0"/>Year 2: F&B and specialty food</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-foreground mt-0.5 shrink-0"/>Year 3: auto parts + industrial wholesale</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-foreground mt-0.5 shrink-0"/>International: UK + EU expansion</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ────────────────────────── COMPETITORS ────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-20 border-t border-foreground/10">
        <Eyebrow>06 · Competition · why we win</Eyebrow>
        <h2 className="display text-4xl sm:text-5xl mt-3 leading-[1.05] tracking-tight max-w-3xl">
          Everyone else sells a feature. We sell the operating system.
        </h2>
        <div className="mt-10 rounded-2xl border border-foreground/10 bg-white overflow-hidden">
          <table className="w-full text-[13.5px]">
            <thead className="bg-secondary/40 text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">
              <tr>
                <th className="text-left p-4">Competitor</th>
                <th className="text-left p-4">What they do</th>
                <th className="text-left p-4">What they miss</th>
                <th className="text-left p-4">Why verqo wins</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-foreground/5">
              {[
                ["Faire ($12B)",        "Marketplace + Net-60 buyer financing", "Sellers lose brand control. 25% take-rate.", "We're SaaS — seller owns the buyer. 5% blended take."],
                ["Joor / NuOrder",      "Digital linesheet + order capture",     "No payments, credit, insurance or shipping",  "10× the ARR per customer."],
                ["Resolve Pay",         "Net-30 credit for B2B only",            "Credit only — no portal, no insurance",       "Full stack > point solution."],
                ["Balance / Apruve",    "Embedded B2B checkout backend",          "Backend only, no merchant UX",                 "We're the full OS, not a plugin."],
                ["Factors (CIT, Hilldun)", "Paper-based invoice financing",      "3–5% fee, no software",                        "Modern rails at 1% — instant approval."],
              ].map((r, i) => (
                <tr key={i} className="hover:bg-secondary/20">
                  <td className="p-4 font-medium">{r[0]}</td>
                  <td className="p-4 text-foreground/75">{r[1]}</td>
                  <td className="p-4 text-foreground/75">{r[2]}</td>
                  <td className="p-4 text-foreground/90">{r[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-10">
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 mb-4">Our moat</div>
          <div className="grid md:grid-cols-4 gap-3">
            {[
              { Icon: LineChart,  t: "Data",       d: "Every invoice improves our underwriting model — compounds forever." },
              { Icon: Network,    t: "Network",    d: "Each seller brings 10–50 buyers. Each buyer unlocks every connected seller." },
              { Icon: Clock,      t: "Switching",  d: "Payments + credit + insurance + shipping through us = leaving means operational suicide." },
              { Icon: Landmark,   t: "Regulatory", d: "Our BaaS + insurance + facility stack took 18 months. Any clone faces the same setup." },
            ].map((m) => (
              <div key={m.t} className="rounded-xl border border-foreground/10 bg-secondary/30 p-5">
                <m.Icon className="w-4 h-4 text-foreground/70"/>
                <div className="display text-lg tracking-tight mt-3">{m.t}</div>
                <p className="text-[12.5px] text-foreground/60 mt-1.5 leading-relaxed">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────────── THE ASK ────────────────────────── */}
      <section id="ask" className="relative py-24 border-t border-foreground/10 overflow-hidden bg-foreground text-background">
        <div className="absolute inset-0 grid-pattern opacity-[0.07]"/>
        <div className="absolute -right-24 -top-24 w-[420px] h-[420px] rounded-full bg-accent/20 blur-3xl"/>
        <div className="absolute -left-20 bottom-1/4 w-72 h-72 rounded-full bg-accent/10 blur-3xl"/>
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-accent">07 · The ask</div>
          <h2 className="display text-4xl sm:text-5xl mt-3 leading-[1.05] tracking-tight max-w-3xl">
            $25M revolving credit facility, 3-year committed.
          </h2>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {/* Use of funds */}
            <div className="rounded-2xl bg-background/5 ring-1 ring-background/15 p-7">
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-accent mb-4">Use of funds</div>
              <div className="space-y-3">
                {[
                  { pct: "70%", label: "Buyer receivables (Net-30/60/90 AR advances)" },
                  { pct: "20%", label: "Inventory financing for approved sellers" },
                  { pct: "10%", label: "PO financing (pay suppliers upstream)" },
                ].map((u) => (
                  <div key={u.label} className="flex items-start gap-3">
                    <div className="display text-xl text-accent w-12 shrink-0">{u.pct}</div>
                    <div className="text-background/85 text-[14px] leading-relaxed pt-1">{u.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Proposed terms */}
            <div className="rounded-2xl bg-background/5 ring-1 ring-background/15 p-7">
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-accent mb-4">Proposed terms</div>
              <ul className="space-y-2.5 text-[14px] text-background/85">
                {[
                  "3-year committed, revolving",
                  "Coupon: SOFR + 450–550 bps",
                  "Advance rate: 85–90% of eligible AR",
                  "Eligibility: insured receivables only (Coface / Allianz wrap)",
                  "Concentration: 10% per obligor, 15% per industry",
                  "First-loss equity reserve: 8–10% from verqo",
                  "Security: perfected lien on AR portfolio",
                  "Covenants: net default <1.5%, 30-DPD <5%",
                ].map((t) => (
                  <li key={t} className="flex gap-2"><Check className="w-4 h-4 text-accent mt-0.5 shrink-0"/>{t}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-10 rounded-2xl bg-accent text-foreground p-7">
            <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/70 mb-3">Return to the capital provider</div>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <div className="display text-3xl tracking-tight">~9.5%</div>
                <div className="text-[12px] mono uppercase tracking-[0.14em] text-foreground/65 mt-1">Coupon (SOFR + 4.5%)</div>
              </div>
              <div>
                <div className="display text-3xl tracking-tight">Senior</div>
                <div className="text-[12px] mono uppercase tracking-[0.14em] text-foreground/65 mt-1">Secured position, insured AR</div>
              </div>
              <div>
                <div className="display text-3xl tracking-tight">Ring-fenced</div>
                <div className="text-[12px] mono uppercase tracking-[0.14em] text-foreground/65 mt-1">SPV / bankruptcy-remote</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────────────── UNIT ECONOMICS ────────────────────────── */}
      <section id="economics" className="max-w-7xl mx-auto px-6 py-20 border-t border-foreground/10">
        <Eyebrow>08 · Unit economics</Eyebrow>
        <h2 className="display text-4xl sm:text-5xl mt-3 leading-[1.05] tracking-tight max-w-3xl">
          Spread is real. Losses are insured.
        </h2>

        <div className="mt-10 rounded-2xl border border-foreground/10 bg-white overflow-hidden">
          <table className="w-full text-[14px]">
            <thead className="bg-secondary/40 text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">
              <tr>
                <th className="text-left p-4">Metric</th>
                <th className="text-right p-4">Current</th>
                <th className="text-right p-4">Year 1 Target</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-foreground/5">
              {[
                ["Avg receivable size",              "$3,800",  "$8,500"],
                ["Avg tenor",                         "52 days",  "60 days"],
                ["30-day delinquency rate",           "1.8%",     "<2.5%"],
                ["Loss rate (net of insurance)",     "<0.4%",    "<0.6%"],
                ["Recovery rate",                     "94%",      "95%"],
                ["Gross yield on AR",                 "14% APR",  "16% APR"],
                ["Net spread to verqo after coupon",  "5.2%",     "6.8%"],
              ].map((r, i) => (
                <tr key={i} className="hover:bg-secondary/20">
                  <td className="p-4 font-medium">{r[0]}</td>
                  <td className="p-4 text-right display">{r[1]}</td>
                  <td className="p-4 text-right">
                    <span className="display inline-block px-2.5 py-0.5 rounded-md bg-accent text-foreground font-semibold">
                      {r[2]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-foreground text-background p-7 relative overflow-hidden">
            <div className="absolute -right-10 -top-10 w-36 h-36 rounded-full bg-accent/20 blur-3xl"/>
            <ShieldCheck className="w-6 h-6 text-accent relative"/>
            <h3 className="display text-xl tracking-tight mt-4 relative">Trade credit insurance wraps every loan</h3>
            <p className="text-background/70 mt-3 text-[14px] leading-relaxed relative">
              Coface or Allianz Trade covers <strong className="text-accent">90% of any default</strong>. Premium: ~1.1% of advanced capital. You never see a loss below the 8–10% first-loss equity reserve.
            </p>
          </div>
          <div className="rounded-2xl border border-foreground/10 bg-white/60 p-7">
            <Bot className="w-6 h-6 text-foreground"/>
            <h3 className="display text-xl tracking-tight mt-4">AI underwriting on every buyer</h3>
            <p className="text-foreground/70 mt-3 text-[14px] leading-relaxed">
              Plaid cashflow + Middesk KYB + behavioral signals run through our Verqo AI underwriter. 90-second decisions with 24 months of bank transaction history behind every approval.
            </p>
          </div>
        </div>
      </section>

      {/* ────────────────────────── RISK MANAGEMENT ────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-20 border-t border-foreground/10">
        <Eyebrow>09 · Risk management</Eyebrow>
        <h2 className="display text-4xl sm:text-5xl mt-3 leading-[1.05] tracking-tight max-w-3xl">
          Six layers between every dollar you advance and any loss.
        </h2>
        <div className="mt-10 grid md:grid-cols-3 gap-3">
          {[
            { n: "01", t: "AI underwriting + Plaid cashflow", d: "Every buyer has 24-month bank history before approval" },
            { n: "02", t: "Middesk KYB",                       d: "Legal entity + UCC liens + SOS status verified" },
            { n: "03", t: "Concentration limits",              d: "Max 10% per buyer, 15% per industry" },
            { n: "04", t: "Insurance wrap",                    d: "Coface / Allianz on every receivable > $5K" },
            { n: "05", t: "First-loss equity reserve",         d: "8–10% of facility held by verqo — skin in the game" },
            { n: "06", t: "Real-time delinquency monitoring",  d: "Plaid-connected bank accounts, alerts before NSF" },
          ].map((r) => (
            <div key={r.n} className="rounded-xl border border-foreground/10 bg-white/60 p-5">
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/45">{r.n}</div>
              <div className="display text-base tracking-tight mt-2">{r.t}</div>
              <p className="text-[12.5px] text-foreground/65 mt-1.5 leading-relaxed">{r.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ────────────────────────── TRACTION ────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-20 border-t border-foreground/10">
        <Eyebrow>10 · What's built</Eyebrow>
        <h2 className="display text-4xl sm:text-5xl mt-3 leading-[1.05] tracking-tight max-w-3xl">
          Built. Live. Shippable.
        </h2>
        <p className="text-foreground/65 mt-5 text-[16px] leading-relaxed max-w-2xl">
          The platform is live today. We'd love to walk you through the full admin view — live buyer KYB, real Plaid cashflow, live AI memo generation, actual Modern Treasury payment orders.
        </p>

        <div className="mt-10 grid md:grid-cols-4 gap-4">
          {[
            { v: "8",      k: "Enterprise rails wired live"   },
            { v: "130+",   k: "Backend tests · 0 failures"    },
            { v: "3",      k: "Production portals shipped"    },
            { v: "90 sec", k: "Avg underwriting decision time" },
          ].map((s) => (
            <div key={s.k} className="rounded-2xl border border-foreground/10 bg-white/60 p-5">
              <div className="display text-4xl tracking-tight">{s.v}</div>
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 mt-2">{s.k}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-4">
          <Link to="/demo" className="rounded-2xl border border-foreground/10 hover:border-foreground/30 bg-white/60 p-6 flex items-center justify-between transition-colors group">
            <div>
              <div className="display text-xl tracking-tight">Live portal demo</div>
              <div className="text-[13px] text-foreground/60 mt-1">Full platform walk-through, no signup</div>
            </div>
            <ArrowUpRight className="w-5 h-5 text-foreground/40 group-hover:text-foreground group-hover:translate-x-0.5 transition-all"/>
          </Link>
          <a href="#contact" className="rounded-2xl border border-foreground/10 hover:border-foreground/30 bg-white/60 p-6 flex items-center justify-between transition-colors group">
            <div>
              <div className="display text-xl tracking-tight">Data room access</div>
              <div className="text-[13px] text-foreground/60 mt-1">AR tape, default history, covenant model</div>
            </div>
            <ArrowUpRight className="w-5 h-5 text-foreground/40 group-hover:text-foreground group-hover:translate-x-0.5 transition-all"/>
          </a>
        </div>
      </section>

      {/* ────────────────────────── PARTNERS ────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-20 border-t border-foreground/10">
        <Eyebrow>11 · Infrastructure partners</Eyebrow>
        <h2 className="display text-4xl sm:text-5xl mt-3 leading-[1.05] tracking-tight max-w-3xl">
          We partner with the rails you already know and trust.
        </h2>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { n: "Modern Treasury", r: "Ledger · payment orders" },
            { n: "Plaid",           r: "Bank linking · cashflow" },
            { n: "Middesk",         r: "KYB · UCC · business credit" },
            { n: "Verqo AI",        r: "AI underwriter" },
            { n: "Verqo Shipping",   r: "Parcel + freight (whitelabeled)" },
            { n: "Coface / Allianz",r: "Trade credit insurance" },
            { n: "SOC-2 Type I",    r: "In progress — Q2 audit" },
          ].map((p) => (
            <div key={p.n} className="rounded-xl border border-foreground/10 bg-secondary/30 p-4">
              <div className="display text-[15px] tracking-tight">{p.n}</div>
              <div className="text-[11.5px] text-foreground/55 mt-1">{p.r}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ────────────────────────── CONTACT ────────────────────────── */}
      <section id="contact" className="relative py-24 border-t border-foreground/10 bg-foreground text-background overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-[0.07]"/>
        <div className="absolute -right-24 -bottom-24 w-[420px] h-[420px] rounded-full bg-accent/30 blur-3xl"/>
        <div className="max-w-3xl mx-auto px-6 relative text-center">
          <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-accent">12 · Next steps</div>
          <h2 className="display text-4xl sm:text-5xl mt-3 leading-[1.02] tracking-tight">
            Let's move money, together.
          </h2>
          <p className="text-background/65 mt-5 text-[16px] leading-relaxed">
            We're happy to share our full data room, reserve policy, covenant model, and a reference call with Modern Treasury and our insurance partner.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-3">
            <a href="mailto:founders@verqo.com?subject=verqo%20credit%20facility%20—%20data%20room%20access"
               className="rounded-xl bg-background/10 ring-1 ring-background/15 hover:ring-accent/40 hover:bg-background/15 p-5 text-left transition-all group">
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-accent">Email</div>
              <div className="display text-lg mt-2">founders@verqo.com</div>
              <div className="text-[11.5px] text-background/55 mt-1">Request the data room</div>
            </a>
            <Link to="/demo"
               className="rounded-xl bg-background/10 ring-1 ring-background/15 hover:ring-accent/40 hover:bg-background/15 p-5 text-left transition-all group">
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-accent">Walk-through</div>
              <div className="display text-lg mt-2">Live portal demo</div>
              <div className="text-[11.5px] text-background/55 mt-1">Full admin + AI memo generation</div>
            </Link>
            <a href="mailto:founders@verqo.com?subject=verqo%20—%20schedule%20a%20call"
               className="rounded-xl bg-background/10 ring-1 ring-background/15 hover:ring-accent/40 hover:bg-background/15 p-5 text-left transition-all group">
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-accent">Book a call</div>
              <div className="display text-lg mt-2">30 min intro</div>
              <div className="text-[11.5px] text-background/55 mt-1">Reply — we'll send a Calendly link</div>
            </a>
          </div>
          <div className="mt-10 text-[11px] mono uppercase tracking-[0.22em] text-background/40">
            Confidential · For investor review only
          </div>
        </div>
      </section>

      <footer className="border-t border-foreground/10">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-wrap justify-between items-center gap-3 text-[12px] text-foreground/55">
          <div>verqo · The operating system for B2B wholesale</div>
          <div className="mono uppercase tracking-[0.18em] text-[10.5px]">v1 · {new Date().getFullYear()}</div>
        </div>
      </footer>
    </div>
  );
}

function Eyebrow({ children }) {
  return (
    <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/55 inline-flex items-center gap-2">
      <span className="w-1.5 h-1.5 rounded-full bg-accent shadow-[0_0_10px_var(--accent,#e8ff3a)]"/>
      {children}
    </div>
  );
}
